import EmailProformaDialog from "./EmailProformaDialog";
import { tenantLocale, tenantTz } from "../lib/company";
import { lastFreeDayIso } from "../lib/tenantTime";

/**
 * Email Proforma for a police impound.
 *
 * Same dialog, PDF and Resend pipeline as the tow-job proforma — only the
 * numbers are sourced differently. A tow job's figures come from the Invoice
 * Summary card; an impound's come from `pricing_breakdown`, which is what the
 * impound list, the release receipt and the owner letter all quote. Deriving
 * them any other way would risk emailing the owner a figure that disagrees
 * with the paperwork.
 *
 * Two things differ from a tow proforma and both matter:
 *  • Statutory impound fees are capped GST-INCLUSIVE, so `taxInclusive` backs
 *    the tax out of the total instead of adding 15% on top of a legal maximum.
 *  • Part payments already taken appear as a credit line, so the owner is
 *    asked for the balance rather than the gross.
 */
const LABELS = {
  tow_fee: "Towage",
  impound_fee: "Impound storage",
  additional_km_fee: "Additional distance",
  overstay_fee: "Storage beyond release date",
};
const ORDER = ["tow_fee", "impound_fee", "additional_km_fee", "overstay_fee"];

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(tenantLocale(), {
        timeZone: tenantTz(), day: "2-digit", month: "short", year: "numeric",
      })
    : null;

export default function ImpoundProformaDialog({ job, open, onOpenChange }) {
  if (!job) return null;

  const b = job.pricing_breakdown || {};
  const li = b.line_items || {};
  const explain = b.explanations || {};

  const lineItems = ORDER
    .filter((k) => Number(li[k] || 0) !== 0)
    .map((k) => ({
      // The explanation ("114 days × $24.30") is the part an owner actually
      // queries, so it belongs on the invoice line, not just on our screen.
      label: explain[k] ? `${LABELS[k]} — ${explain[k]}` : LABELS[k],
      amount: Number(li[k] || 0),
    }));

  // The list endpoint only stamps `part_payments_total` once an impound is
  // released; while it is still in the yard the payments live in the array.
  // Reading only the total asked a owner who had already paid $200 for the
  // full $24,748.22 again (caught in test iteration 84).
  const paid = Number(
    job.part_payments_total
    ?? (job.part_payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
  );
  if (paid > 0) {
    lineItems.push({ label: "Less part payment received", amount: -paid });
  }

  const balance = lineItems.reduce((sum, i) => sum + Number(i.amount || 0), 0);

  const dailyRate = Number(b.overstay_per_day || 0);
  // `impound_release_due_at` is the midnight that STARTS the first overdue
  // day, so the last free day is the day before it — the same figure the
  // list and the detail panel show. Printing the raw timestamp would tell
  // the owner they have a day longer than they do, on a document they may
  // well wave at us later.
  const dueIso = job.impound_release_due_at || b.release_due_at;
  const dueDate = fmtDate(dueIso ? lastFreeDayIso(dueIso) : null);
  const noteBits = [];
  if (dueDate) {
    noteBits.push(
      `This vehicle must be collected by ${dueDate}.`
      + (dailyRate > 0
        ? ` Storage after that date accrues at $${dailyRate.toFixed(2)} per day.`
        : ""),
    );
  }
  if (paid > 0) {
    noteBits.push(`Part payment of $${paid.toFixed(2)} has been received and is credited above.`);
  }

  return (
    <EmailProformaDialog
      open={open}
      onOpenChange={onOpenChange}
      job={job}
      lineItems={lineItems}
      grandTotal={balance}
      taxRate={0.15}
      taxLabel="GST"
      taxInclusive
      defaultTo=""
      defaultNote={noteBits.join(" ")}
      description={
        `Proforma for ${(job.rego || "").toUpperCase() || job.vehicle || "this vehicle"} `
        + `addressed to ${job.impound_owner_name || job.customer_name || "the registered owner"}. `
        + "Check the email address before sending — impound records hold a postal "
        + "address and phone, not an email."
      }
    />
  );
}
